import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  Chip,
  Divider,
  Collapse,
  TableSortLabel,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  MedicalServices as MedicalIcon,
  Clear as ClearIcon,
  AttachFile as AttachFileIcon,
  Restore as RestoreIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Pets as PetsIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import DateField from './DateField';
import HelpButton from './HelpButton';
import { formatDate } from '../utils/dateUtils';
import { translateCostClass } from '../utils/translationUtils';
import CostClassHistoryPanel from './CostClassHistoryPanel';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface Angel {
  id: number;
  chip_id?: string;
  name?: string;
  sex?: string;
  age?: string;
  breed?: string;
  color?: string;
  arrival_date?: string;
  departure_date?: string;
  municipality?: string;
  source?: string;
  address_where_found?: string;
  initial_health_status?: string;
  fiv?: string;
  felv?: string;
  cost_class?: string;
  temporary_placement?: string;
  area?: string;
  sterilized?: string;
  notes?: string;
  photo_file_path?: string;
  death_date?: string;
  death_certificate?: string;
  death_certificate_filename?: string;
}

interface Column {
  column_name: string;
  data_type: string;
}

interface FilterCriteria {
  id: string;
  field: string;
  value: string;
}

const AngelsList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [angels, setAngels] = useState<Angel[]>([]);
  const [filteredAngels, setFilteredAngels] = useState<Angel[]>([]);
  const [loading, setLoading] = useState(true);
  const [angelsColumns, setAngelsColumns] = useState<Column[]>([]);
  const [municipalities, setMunicipalities] = useState<string[]>([]);

  // Detail panel state
  const [detailAngel, setDetailAngel] = useState<Angel | null>(null);

  // Filter state - multi-field with AND logic
  const [filters, setFilters] = useState<FilterCriteria[]>([]);
  const [currentFilterField, setCurrentFilterField] = useState<string>('');
  const [currentFilterValue, setCurrentFilterValue] = useState<string>('');
  const [filtersExpanded, setFiltersExpanded] = useState(true);

  // Column widths for resizable table
  const [colWidths, setColWidths] = useState<Record<string, number>>({
    id: 50, chip_id: 130, name: 120, sex: 48,
    color: 80, municipality: 120, fiv: 52, felv: 52, death_date: 110, cost_class: 110,
  });
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const handleSort = (col: string) => {
    if (sortCol === col) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortCol(col); setSortDir('asc'); }
  };
  const sortedAngels = useMemo(() => {
    if (!sortCol) return filteredAngels;
    return [...filteredAngels].sort((a, b) => {
      const ar = (a as any)[sortCol] ?? '';
      const br = (b as any)[sortCol] ?? '';
      const an = Number(ar), bn = Number(br);
      if (!isNaN(an) && !isNaN(bn) && ar !== '' && br !== '') {
        return sortDir === 'asc' ? an - bn : bn - an;
      }
      const av = String(ar).toLowerCase();
      const bv = String(br).toLowerCase();
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [filteredAngels, sortCol, sortDir]);
  const resizingRef = useRef<{ col: string; startX: number; startWidth: number } | null>(null);
  const handleResizeMouseDown = useCallback((col: string, e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    resizingRef.current = { col, startX: e.clientX, startWidth: colWidths[col] };
    const onMouseMove = (ev: MouseEvent) => {
      if (!resizingRef.current) return;
      setColWidths(prev => ({ ...prev, [resizingRef.current!.col]: Math.max(40, resizingRef.current!.startWidth + ev.clientX - resizingRef.current!.startX) }));
    };
    const onMouseUp = () => { resizingRef.current = null; document.removeEventListener('mousemove', onMouseMove); document.removeEventListener('mouseup', onMouseUp); };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [colWidths]);

  // Edit angel dialog state
  const [editAngelOpen, setEditAngelOpen] = useState(false);
  const [selectedAngel, setSelectedAngel] = useState<Angel | null>(null);
  const [angelFormData, setAngelFormData] = useState<any>({});
  const [editPhotoFile, setEditPhotoFile] = useState<File | null>(null);

  // Add event dialog state
  const [addEventOpen, setAddEventOpen] = useState(false);
  const [eventFormData, setEventFormData] = useState<any>({});
  const [eventAngel, setEventAngel] = useState<Angel | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Message state
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Restore dialog state
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [angelToRestore, setAngelToRestore] = useState<Angel | null>(null);
  const [reEntryDate, setReEntryDate] = useState<string>('');

  useEffect(() => {
    loadAngels();
    loadAngelsColumns();
    loadMunicipalities();
  }, []);

  useEffect(() => {
    applyFilter();
  }, [angels, filters]);

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return null;

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.post(`${API_URL}/api/upload/invoice`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data.file_path;
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: `File upload failed: ${error.response?.data?.detail || error.message}`,
      });
      return null;
    }
  };

  const loadAngels = async () => {
    try {
      setLoading(true);
      // Phase 4: Query unified cats table with status=angel filter
      const response = await api.get(`${API_URL}/api/tables/public/cats/data?status=angel&limit=1000`);
      const columns: string[] = response.data.columns;
      const angelsData = response.data.rows.map((row: any[]) => {
        const angel: any = {};
        columns.forEach((col, i) => {
          angel[col] = row[i];
        });
        return angel as Angel;
      });
      setAngels(angelsData);
      setFilteredAngels(angelsData);
    } catch (error) {
      console.error('Error loading angels:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAngelsColumns = async () => {
    try {
      // Phase 4: Use unified cats table columns
      const response = await api.get(`${API_URL}/api/tables/public/cats/columns`);
      setAngelsColumns(response.data);
    } catch (error) {
      console.error('Error loading angels columns:', error);
    }
  };

  const loadMunicipalities = async () => {
    try {
      const response = await api.get(`${API_URL}/api/tables/public/municipalities/data?limit=1000`);
      const municipalitiesData = response.data.rows.map((row: any[]) => row[0]);
      setMunicipalities(municipalitiesData.sort());
    } catch (error) {
      console.error('Error loading municipalities:', error);
    }
  };

  const applyFilter = () => {
    if (filters.length === 0) {
      setFilteredAngels(angels);
      return;
    }
    const filtered = angels.filter((angel) =>
      filters.every((filter) => {
        const fieldValue = angel[filter.field as keyof Angel];
        if (fieldValue === null || fieldValue === undefined) return false;
        return String(fieldValue).toLowerCase().includes(filter.value.toLowerCase());
      })
    );
    setFilteredAngels(filtered);
  };

  const addFilter = () => {
    if (!currentFilterField || !currentFilterValue) return;
    setFilters([...filters, { id: Date.now().toString(), field: currentFilterField, value: currentFilterValue }]);
    setCurrentFilterField('');
    setCurrentFilterValue('');
  };

  const removeFilter = (filterId: string) => {
    setFilters(filters.filter((f) => f.id !== filterId));
  };

  const clearAllFilters = () => {
    setFilters([]);
    setCurrentFilterField('');
    setCurrentFilterValue('');
  };

  const getFieldLabel = (fieldName: string): string => {
    const labels: Record<string, string> = {
      chip_id: 'Chip ID', name: 'Name', sex: 'Sex', age: 'Age', breed: 'Breed',
      municipality: 'Municipality', source: 'Source', sterilized: 'Sterilized',
      fiv: 'FIV', felv: 'FeLV', area: 'Area', temporary_placement: 'Temp. Placement',
      death_date: 'Death Date',
    };
    return labels[fieldName] || fieldName;
  };

  const handleRowClick = (angel: Angel) => {
    setDetailAngel(angel);
  };

  const handleEditAngel = (angel: Angel) => {
    setSelectedAngel(angel);
    setAngelFormData({
      chip_id: angel.chip_id || '',
      name: angel.name || '',
      sex: angel.sex || '',
      age: angel.age || '',
      breed: angel.breed || '',
      source: angel.source || '',
      municipality: angel.municipality || '',
      arrival_date: angel.arrival_date || '',
      departure_date: angel.departure_date || '',
      address_where_found: angel.address_where_found || '',
      temporary_placement: angel.temporary_placement || '',
      area: angel.area || '',
      notes: angel.notes || '',
      photo_file_path: angel.photo_file_path || '',
      death_date: angel.death_date || '',
    });
    setEditPhotoFile(null);
    setEditAngelOpen(true);
  };

  const handleUpdateAngel = async () => {
    try {
      let updatedData = { ...angelFormData };
      if (updatedData.sterilized === 'U') updatedData.sterilized = null;

      // Upload photo if a new one is selected
      if (editPhotoFile) {
        const formData = new FormData();
        formData.append('file', editPhotoFile);

        const uploadResponse = await api.post(`/api/upload/photo`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        updatedData.photo_file_path = uploadResponse.data.file_path;
      }

      // Store cost_class before filtering in case we need it for history
      const costClassChanged = selectedAngel && updatedData.cost_class && updatedData.cost_class !== selectedAngel.cost_class;
      const newCostClass = updatedData.cost_class;

      // Filter out columns that don't exist in the angels table
      const validColumns = new Set(angelsColumns.map(col => col.column_name));
      const filteredData: any = {};
      Object.entries(updatedData).forEach(([key, value]) => {
        if (validColumns.has(key)) {
          filteredData[key] = value;
        }
      });

      await api.put(`/api/records`, {
        table: 'cats',
        schema: 'public',
        record_id: selectedAngel?.id,
        data: filteredData,
      });

      // Upsert a cost-class history entry for today
      if (costClassChanged) {
        const todayStr = new Date().toISOString().split('T')[0];
        try {
          await api.post(`/api/cost-class-history/cost_class_history/cat_id/${selectedAngel.id}/upsert`, {
            cost_class: newCostClass,
            effective_from: todayStr,
          });
        } catch (e: any) { console.error('Cost class history upsert failed:', e?.response?.data || e?.message); }
      }

      setMessage({ type: 'success', text: 'Angel updated successfully!' });
      setEditAngelOpen(false);
      setEditPhotoFile(null);

      // Reload angels and update selectedAngel with fresh data
      const response = await api.get(`${API_URL}/api/tables/public/cats/data?status=angel&limit=1000`);
      const columns: string[] = response.data.columns;
      const angelsData = response.data.rows.map((row: any[]) => {
        const angel: any = {};
        columns.forEach((col, i) => {
          angel[col] = row[i];
        });
        return angel as Angel;
      });
      setAngels(angelsData);
      setFilteredAngels(angelsData);

      // Update selectedAngel with fresh data from the reloaded list
      const updatedAngel = angelsData.find((a: Angel) => a.id === selectedAngel?.id);
      if (updatedAngel) {
        setSelectedAngel(updatedAngel);
        setAngelFormData(updatedAngel);
      }

      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to update angel';
      setMessage({ type: 'error', text: errorMessage });
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const handleAddEventForAngel = (angel: Angel) => {
    setEventAngel(angel);
    setEventFormData({ event_date: getTodayDate() });
    setSelectedFile(null);
    setAddEventOpen(true);
  };

  const handleAddEvent = async () => {
    try {
      const eventType = eventFormData.event_type;

      // Check for duplicate Sterilization event (Phase 5: unified medical table)
      if (eventType === 'Sterilization') {
        const eventsResponse = await api.get(
          `${API_URL}/api/tables/public/medical/data?limit=1000`
        );
        const medCols: string[] = eventsResponse.data.columns;
        const catIdIdx = medCols.indexOf('cat_id');
        const eventTypeIdx = medCols.indexOf('event_type');
        const existingEvents = eventsResponse.data.rows.filter(
          (row: any[]) => row[catIdIdx] === eventAngel?.id
        );
        const hasSterilization = existingEvents.some((row: any[]) => row[eventTypeIdx] === 'Sterilization');
        if (hasSterilization) {
          setMessage({
            type: 'error',
            text: 'This cat is already sterilized. Each cat can only be sterilized once.',
          });
          return;
        }
      }

      // Upload file if selected
      let filePath = null;
      if (selectedFile) {
        filePath = await handleFileUpload();
        if (!filePath) {
          setMessage({ type: 'error', text: 'File upload failed. Please try again.' });
          return;
        }
      }

      await api.post(`${API_URL}/api/records`, {
        table: 'medical',
        schema: 'public',
        data: {
          cat_id: eventAngel?.id,
          ...eventFormData,
          ...(filePath && { invoice_file_path: filePath }),
        },
      });

      setMessage({ type: 'success', text: 'Medical event added successfully!' });
      setAddEventOpen(false);
      setEventFormData({ event_date: getTodayDate() });
      setSelectedFile(null);
      loadAngels();
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to add medical event' });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleRestoreClick = (angel: Angel) => {
    setAngelToRestore(angel);
    const d = new Date();
    setReEntryDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    setRestoreDialogOpen(true);
  };

  const handleRestoreConfirm = async () => {
    if (!angelToRestore) return;
    if (!reEntryDate) {
      setMessage({ type: 'error', text: t('checkout.reEntryDateRequired') });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    try {
      const response = await api.post(`${API_URL}/api/restore-cat`, {
        source_table: 'angels',
        source_id: angelToRestore.id,
        re_entry_date: reEntryDate,
      });

      setMessage({ type: 'success', text: response.data.message });
      setRestoreDialogOpen(false);
      setAngelToRestore(null);
      loadAngels();
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to restore cat to active database'
      });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const renderFormField = (column: Column, formData: any, setFormData: any) => {
    if (column.column_name === 'id') return null;
    // Skip death certificate fields - handled separately
    if (column.column_name === 'death_certificate' || column.column_name === 'death_certificate_filename') return null;

    if (column.column_name === 'sex') {
      return (
        <FormControl fullWidth margin="normal" key={column.column_name}>
          <InputLabel>{t('catFields.sex')}</InputLabel>
          <Select
            value={formData[column.column_name] || ''}
            label={t('catFields.sex')}
            onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
          >
            <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
            <MenuItem value="M">M</MenuItem>
            <MenuItem value="F">F</MenuItem>
          </Select>
        </FormControl>
      );
    }

    if (column.column_name === 'age') {
      return (
        <FormControl fullWidth margin="normal" key={column.column_name}>
          <InputLabel>{t('catFields.age')}</InputLabel>
          <Select
            value={formData[column.column_name] || ''}
            label={t('catFields.age')}
            onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
          >
            <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
            <MenuItem value="Kitten">{t('catAges.kitten')}</MenuItem>
            <MenuItem value="Adult">{t('catAges.adult')}</MenuItem>
            <MenuItem value="Senior">{t('catAges.senior')}</MenuItem>
          </Select>
        </FormControl>
      );
    }

    if (column.column_name === 'source') {
      return (
        <FormControl fullWidth margin="normal" key={column.column_name}>
          <InputLabel>{t('catFields.source')}</InputLabel>
          <Select
            value={formData[column.column_name] || ''}
            label={t('catFields.source')}
            onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
          >
            <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
            <MenuItem value="ASL">ASL</MenuItem>
            <MenuItem value="Private">{t('catSources.private')}</MenuItem>
            <MenuItem value="Municipality">{t('catSources.municipality')}</MenuItem>
          </Select>
        </FormControl>
      );
    }

    if (column.column_name === 'municipality') {
      return (
        <FormControl fullWidth margin="normal" key={column.column_name}>
          <InputLabel>{t('catFields.municipality')}</InputLabel>
          <Select
            value={formData[column.column_name] || ''}
            label={t('catFields.municipality')}
            onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
          >
            <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
            {municipalities.map((municipality: string) => (
              <MenuItem key={municipality} value={municipality}>
                {municipality}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (column.column_name === 'address_where_found') {
      return (
        <TextField
          key={column.column_name}
          fullWidth
          margin="normal"
          label={t('catFields.addressWhereFound')}
          value={formData[column.column_name] || ''}
          onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
          multiline
          rows={2}
        />
      );
    }

    if (column.column_name === 'sterilized') {
      return null; // Sterilized is auto-updated by trigger
    }

    if (column.column_name === 'photo_file_path') {
      return (
        <Box key={column.column_name} sx={{ mt: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Cat Photo
          </Typography>

          {/* Show existing photo */}
          {formData.photo_file_path && !editPhotoFile && (
            <Box sx={{ mb: 2 }}>
              <Box
                component="img"
                src={`${API_URL}${formData.photo_file_path}`}
                alt="Cat photo"
                sx={{
                  width: '100%',
                  maxWidth: 300,
                  height: 'auto',
                  borderRadius: 2,
                  border: '2px solid #ffecd2',
                  mb: 1,
                }}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Current photo
              </Typography>
            </Box>
          )}

          {/* Show new photo preview */}
          {editPhotoFile && (
            <Box sx={{ mb: 2 }}>
              <Box
                component="img"
                src={URL.createObjectURL(editPhotoFile)}
                alt="New cat photo"
                sx={{
                  width: '100%',
                  maxWidth: 300,
                  height: 'auto',
                  borderRadius: 2,
                  border: '2px solid #4caf50',
                  mb: 1,
                }}
              />
              <Typography variant="caption" color="success.main" display="block" fontWeight="bold">
                New photo selected
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {editPhotoFile.name} ({(editPhotoFile.size / 1024).toFixed(2)} KB)
              </Typography>
            </Box>
          )}

          {/* Upload button */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              component="label"
              size="small"
              sx={{
                background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
                color: '#333',
                textTransform: 'none',
                fontWeight: 600,
                flexGrow: 1,
              }}
            >
              {editPhotoFile ? 'Change Photo' : formData.photo_file_path ? 'Replace Photo' : 'Upload Photo'}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setEditPhotoFile(e.target.files[0]);
                  }
                }}
              />
            </Button>
            {editPhotoFile && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => setEditPhotoFile(null)}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Clear
              </Button>
            )}
          </Box>
        </Box>
      );
    }

    return (
      <TextField
        key={column.column_name}
        fullWidth
        margin="normal"
        label={column.column_name.replace(/_/g, ' ').toUpperCase()}
        type={column.data_type.includes('date') ? 'date' : column.data_type.includes('int') ? 'number' : 'text'}
        value={formData[column.column_name] || ''}
        onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
        InputLabelProps={column.data_type.includes('date') ? { shrink: true } : undefined}
      />
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
      {message && (
        <Alert
          severity={message.type}
          sx={{
            mb: 2,
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(255,236,210,0.15)',
          }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: '#fcb69f' }}>
            {t('lists.angelsList')} ({filteredAngels.length})
          </Typography>
          <HelpButton
            title={t('help.angels.title')}
            intro={t('help.angels.intro')}
            items={t('help.angels.items', { returnObjects: true }) as string[]}
          />
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          sx={{
            borderColor: '#fcb69f',
            color: '#fcb69f',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              borderColor: '#fb9f88',
              background: 'rgba(252,182,159,0.05)',
            }
          }}
        >
          {t('common.backToMenu')}
        </Button>
      </Box>

      {/* Filter Section */}
      <Paper elevation={0} sx={{ mb: 1.5, borderRadius: 2, boxShadow: '0 2px 8px rgba(255,236,210,0.12)' }}>
        <Box
          sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setFiltersExpanded(!filtersExpanded)}
        >
          <FilterIcon sx={{ fontSize: 18, color: '#fcb69f' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
            {t('lists.filterAngels')}
          </Typography>
          {filters.length > 0 && !filtersExpanded && (
            <Chip label={filters.length} size="small" sx={{ height: 20, bgcolor: '#fcb69f', color: 'white', fontSize: '0.7rem' }} />
          )}
          {filtersExpanded ? <ExpandLessIcon sx={{ color: 'text.secondary', fontSize: 20 }} /> : <ExpandMoreIcon sx={{ color: 'text.secondary', fontSize: 20 }} />}
        </Box>
        <Collapse in={filtersExpanded}>
          <Box sx={{ px: 1.5, pb: 1.5 }}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('lists.field')}</InputLabel>
                  <Select value={currentFilterField} label={t('lists.field')} onChange={(e) => setCurrentFilterField(e.target.value)}>
                    <MenuItem value=""><em>{t('lists.selectFieldPlaceholder')}</em></MenuItem>
                    <MenuItem value="chip_id">{t('cat.chipId')}</MenuItem>
                    <MenuItem value="name">{t('cat.name')}</MenuItem>
                    <MenuItem value="sex">{t('cat.sex')}</MenuItem>
                    <MenuItem value="age">{t('cat.age')}</MenuItem>
                    <MenuItem value="breed">{t('cat.breed')}</MenuItem>
                    <MenuItem value="cost_class">{t('cat.costClass')}</MenuItem>
                    <MenuItem value="municipality">{t('cat.municipality')}</MenuItem>
                    <MenuItem value="source">{t('cat.source')}</MenuItem>
                    <MenuItem value="sterilized">{t('cat.sterilized')}</MenuItem>
                    <MenuItem value="fiv">FIV</MenuItem>
                    <MenuItem value="felv">FeLV</MenuItem>
                    <MenuItem value="area">{t('cat.area')}</MenuItem>
                    <MenuItem value="temporary_placement">{t('cat.temporaryPlacement')}</MenuItem>
                    <MenuItem value="death_date">{t('checkout.deathDate')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth size="small" label={t('lists.value')} value={currentFilterValue}
                  onChange={(e) => setCurrentFilterValue(e.target.value)}
                  disabled={!currentFilterField} placeholder={t('lists.enterValue')}
                  onKeyPress={(e) => { if (e.key === 'Enter') addFilter(); }}
                />
              </Grid>
              <Grid item xs={6} md={1.5}>
                <Button fullWidth variant="contained" onClick={addFilter}
                  disabled={!currentFilterField || !currentFilterValue} startIcon={<AddIcon />}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', color: '#333' }}
                >
                  {t('common.add')}
                </Button>
              </Grid>
              <Grid item xs={6} md={1.5}>
                <Button fullWidth variant="outlined" onClick={clearAllFilters}
                  disabled={filters.length === 0 && !currentFilterField && !currentFilterValue} startIcon={<ClearIcon />}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  {t('common.clear')}
                </Button>
              </Grid>
            </Grid>
            {filters.length > 0 && (
              <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mr: 0.5 }}>{t('lists.active')}</Typography>
                {filters.map((filter) => (
                  <Chip
                    key={filter.id}
                    label={`${getFieldLabel(filter.field)}: ${filter.value}`}
                    onDelete={() => removeFilter(filter.id)}
                    size="small"
                    sx={{ background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', color: '#333',
                      '& .MuiChip-deleteIcon': { color: 'rgba(0,0,0,0.4)', '&:hover': { color: '#333' } } }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* Master-Detail Layout */}
      <Box sx={{ display: 'flex', gap: 1.5, height: 'calc(100vh - 280px)', minHeight: 400 }}>
        {/* Master Table (Left Side) */}
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            flex: detailAngel ? '0 0 55%' : '1 1 100%',
            transition: 'flex 0.3s ease',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(255,236,210,0.12)',
            overflow: 'auto',
          }}
        >
          <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 36 }} />
              {(['id','chip_id','name','sex','color','municipality','fiv','felv','death_date','cost_class'] as const).map(col => (
                <col key={col} style={{ width: colWidths[col] }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow sx={{ '& th': { background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', color: '#333', fontWeight: 700, padding: '6px 10px', fontSize: '0.75rem', position: 'relative', overflow: 'hidden', whiteSpace: 'nowrap' } }}>
                <TableCell sx={{ color: 'rgba(0,0,0,0.4)', fontWeight: 400 }}>#</TableCell>
                {([
                  { col: 'id', label: 'ID' },
                  { col: 'chip_id', label: t('cat.chipId') },
                  { col: 'name', label: t('cat.name') },
                  { col: 'sex', label: t('cat.sex') },
                  { col: 'color', label: t('cat.color') },
                  { col: 'municipality', label: t('cat.municipality') },
                  { col: 'fiv', label: t('cat.fiv') },
                  { col: 'felv', label: t('cat.felv') },
                  { col: 'death_date', label: t('checkout.deathDate') },
                  { col: 'cost_class', label: t('cat.costClass') },
                ] as const).map(({ col, label }) => (
                  <TableCell key={col} onClick={() => handleSort(col)} sx={{ cursor: 'pointer', userSelect: 'none' }}>
                    <TableSortLabel
                      active={sortCol === col}
                      direction={sortCol === col ? sortDir : 'asc'}
                    >
                      {label}
                    </TableSortLabel>
                    <Box onMouseDown={(e) => handleResizeMouseDown(col, e)} onClick={(e) => e.stopPropagation()} sx={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 5, cursor: 'col-resize', zIndex: 1, '&:hover': { background: 'rgba(0,0,0,0.15)' } }} />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={11} align="center"><Typography color="text.secondary">{t('common.loading')}</Typography></TableCell></TableRow>
              ) : filteredAngels.length === 0 ? (
                <TableRow><TableCell colSpan={11} align="center"><Typography color="text.secondary">{t('lists.noAngelsFound')}</Typography></TableCell></TableRow>
              ) : (
                sortedAngels.map((angel: Angel, index: number) => (
                  <TableRow
                    key={angel.id}
                    hover
                    onClick={() => handleRowClick(angel)}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: detailAngel?.id === angel.id ? 'rgba(252,182,159,0.15)' : 'inherit',
                      borderLeft: detailAngel?.id === angel.id ? '3px solid #fcb69f' : '3px solid transparent',
                      '& td': { padding: '4px 8px', fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0 },
                      '&:hover': { backgroundColor: detailAngel?.id === angel.id ? 'rgba(252,182,159,0.2)' : 'rgba(0,0,0,0.04)' },
                    }}
                  >
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.7rem', userSelect: 'none' }}>{index + 1}</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>{angel.id}</TableCell>
                    <TableCell sx={{ fontWeight: detailAngel?.id === angel.id ? 600 : 400 }}>{angel.chip_id || t('cat.missing')}</TableCell>
                    <TableCell sx={{ fontWeight: detailAngel?.id === angel.id ? 600 : 400 }}>{angel.name || 'N/A'}</TableCell>
                    <TableCell>{angel.sex || 'N/A'}</TableCell>
                    <TableCell>{angel.color || 'N/A'}</TableCell>
                    <TableCell>{angel.municipality || 'N/A'}</TableCell>
                    <TableCell><Chip label={angel.fiv === 'Y' ? 'Y' : angel.fiv === 'N' ? 'N' : '?'} size="small" color={angel.fiv === 'Y' ? 'error' : angel.fiv === 'N' ? 'success' : 'default'} sx={{ fontWeight: 600, height: 20, fontSize: '0.7rem', minWidth: 28 }} /></TableCell>
                    <TableCell><Chip label={angel.felv === 'Y' ? 'Y' : angel.felv === 'N' ? 'N' : '?'} size="small" color={angel.felv === 'Y' ? 'error' : angel.felv === 'N' ? 'success' : 'default'} sx={{ fontWeight: 600, height: 20, fontSize: '0.7rem', minWidth: 28 }} /></TableCell>
                    <TableCell>{formatDate(angel.death_date)}</TableCell>
                    <TableCell>{translateCostClass(t, angel.cost_class)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Detail Panel (Right Side) */}
        {detailAngel && (
          <Paper
            elevation={0}
            sx={{
              flex: '0 0 42%',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(255,236,210,0.12)',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Header with Photo */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #a78bfa 100%)',
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              {detailAngel.photo_file_path ? (
                <Box
                  component="img"
                  src={`${API_URL}${detailAngel.photo_file_path}`}
                  alt={detailAngel.name || 'Angel photo'}
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 1.5,
                    objectFit: 'cover',
                    border: '2px solid white',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 1.5,
                    backgroundColor: 'rgba(255,255,255,0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid white',
                  }}
                >
                  <PetsIcon sx={{ fontSize: 28, color: 'white' }} />
                </Box>
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', color: 'white' }}>
                  {detailAngel.name || t('cat.unnamed')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                  ID: {detailAngel.id} {`| Chip: ${detailAngel.chip_id || t('cat.missing')}`}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => setDetailAngel(null)}
                sx={{ color: 'white' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ p: 1, display: 'flex', gap: 0.5, borderBottom: '1px solid #eee' }}>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => handleEditAngel(detailAngel)}
                size="small"
                sx={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                {t('common.edit')}
              </Button>
              <Button
                variant="contained"
                startIcon={<MedicalIcon />}
                onClick={() => handleAddEventForAngel(detailAngel)}
                size="small"
                sx={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                {t('medical.medicalEvent')}
              </Button>
              <Button
                variant="contained"
                startIcon={<RestoreIcon />}
                onClick={() => handleRestoreClick(detailAngel)}
                sx={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                  color: '#333',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #97dcd9 0%, #edc5d2 100%)',
                  },
                }}
              >
                {t('data.restore')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={() => navigate(`/cat-record/${detailAngel.id}`)}
                size="small"
                sx={{ flex: 1, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
              >
                {t('external.openRecord')}
              </Button>
            </Box>

            {/* Detail Sections */}
            <Box sx={{ p: 1.5, flex: 1, overflow: 'auto' }}>
              {/* Basic Information */}
              <Typography variant="subtitle2" sx={{ color: '#667eea', fontWeight: 700, mb: 0.75, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                {t('cat.basicInfo')}
              </Typography>
              <Grid container spacing={1} sx={{ mb: 1 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.sex')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{detailAngel.sex || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.age')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{detailAngel.age || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.breed')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{detailAngel.breed || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.color')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{detailAngel.color || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.sterilized')}</Typography>
                  <Chip
                    label={detailAngel.sterilized === 'Y' ? t('common.yes') : detailAngel.sterilized === 'N' ? t('common.no') : t('common.unknown')}
                    size="small"
                    color={detailAngel.sterilized === 'Y' ? 'success' : 'default'}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 1 }} />

              {/* Location & Source */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#fcb69f', mb: 0.75, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t('cat.locationSource')}
              </Typography>
              <Grid container spacing={1} sx={{ mb: 1 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.municipality')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{detailAngel.municipality || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.source')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{detailAngel.source || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.addressWhereFound')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{detailAngel.address_where_found || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.temporaryPlacement')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{detailAngel.temporary_placement || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.area')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{detailAngel.area || 'N/A'}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 1 }} />

              {/* Health Status */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#fcb69f', mb: 0.75, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t('cat.healthStatus')}
              </Typography>
              <Grid container spacing={1} sx={{ mb: 1 }}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.initialHealthStatus')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{detailAngel.initial_health_status || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.fiv')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{detailAngel.fiv || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.felv')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{detailAngel.felv || 'N/A'}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 1 }} />

              {/* Dates */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#fcb69f', mb: 0.75, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t('cat.dates')}
              </Typography>
              <Grid container spacing={1} sx={{ mb: 1 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.arrivalDate')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{formatDate(detailAngel.arrival_date)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.departureDate')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{formatDate(detailAngel.departure_date)}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 1 }} />

              {/* Death Information */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#f5576c', mb: 0.75, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t('checkout.deathInfo')}
              </Typography>
              <Grid container spacing={1} sx={{ mb: 1 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('checkout.deathDate')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{formatDate(detailAngel.death_date)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  {detailAngel.death_certificate && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      href={`${API_URL}/api/angels/${detailAngel.id}/death-certificate`}
                      target="_blank"
                      sx={{
                        mt: 0.5,
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        borderColor: '#f5576c',
                        color: '#f5576c',
                        '&:hover': {
                          borderColor: '#e4465b',
                          backgroundColor: 'rgba(245,87,108,0.05)',
                        },
                      }}
                    >
                      {detailAngel.death_certificate_filename || t('checkout.downloadDeathCertificate')}
                    </Button>
                  )}
                </Grid>
              </Grid>

              {/* Cost Class History */}
              <Divider sx={{ my: 1 }} />
              <CostClassHistoryPanel
                entityId={detailAngel.id}
                entityTable="angels"
                onHistoryChanged={loadAngels}
              />

              {/* Notes */}
              {detailAngel.notes && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#fcb69f', mb: 0.75, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {t('cat.notes')}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
                    {detailAngel.notes}
                  </Typography>
                </>
              )}
            </Box>
          </Paper>
        )}
      </Box>

      {/* Edit Angel Dialog */}
      <Dialog
        open={editAngelOpen}
        onClose={() => {
          setEditAngelOpen(false);
          setEditPhotoFile(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(255,236,210,0.25)',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', color: '#333', fontWeight: 700 }}>
          {t('checkout.editAngel')}: {selectedAngel?.name || selectedAngel?.chip_id}
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important', pb: 1, px: 2 }}>
          {/* Identity Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#ffecd2', mb: 0.75, fontSize: '0.8rem' }}>
            {t('cat.identity')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('cat.chipId')} value={angelFormData.chip_id || ''} onChange={(e) => setAngelFormData({ ...angelFormData, chip_id: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('cat.name')} value={angelFormData.name || ''} onChange={(e) => setAngelFormData({ ...angelFormData, name: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('cat.sex')}</InputLabel>
                <Select value={angelFormData.sex || ''} label={t('cat.sex')} onChange={(e) => setAngelFormData({ ...angelFormData, sex: e.target.value })}>
                  <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
                  <MenuItem value="M">M</MenuItem>
                  <MenuItem value="F">F</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={8}>
              {(() => {
                const ageValue = angelFormData.age || '';
                const parts = ageValue.split('/');
                const month = parts[0] || '';
                const year = parts[1] || '';
                return (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <FormControl sx={{ width: '45%' }} size="small">
                      <InputLabel>{t('cat.birthMonth')}</InputLabel>
                      <Select
                        value={month}
                        label={t('cat.birthMonth')}
                        onChange={(e) => setAngelFormData({ ...angelFormData, age: `${e.target.value}/${year}` })}
                      >
                        <MenuItem value=""><em>{t('common.month')}</em></MenuItem>
                        {['01','02','03','04','05','06','07','08','09','10','11','12'].map(m => (
                          <MenuItem key={m} value={m}>{m}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      sx={{ width: '55%' }}
                      size="small"
                      label={t('cat.birthYear')}
                      placeholder={t('common.year') || 'YYYY'}
                      value={year}
                      onChange={(e) => {
                        const newYear = e.target.value.replace(/\D/g, '').slice(0, 4);
                        setAngelFormData({ ...angelFormData, age: `${month}/${newYear}` });
                      }}
                      inputProps={{ maxLength: 4 }}
                    />
                  </Box>
                );
              })()}
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('cat.breed')} value={angelFormData.breed || ''} onChange={(e) => setAngelFormData({ ...angelFormData, breed: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('cat.color')} value={angelFormData.color || ''} onChange={(e) => setAngelFormData({ ...angelFormData, color: e.target.value })} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          {/* Location & Source Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#ffecd2', mb: 0.75, fontSize: '0.8rem' }}>
            {t('cat.locationSource')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('cat.source')}</InputLabel>
                <Select value={angelFormData.source || ''} label={t('cat.source')} onChange={(e) => setAngelFormData({ ...angelFormData, source: e.target.value })}>
                  <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
                  <MenuItem value="ASL">{t('cat.sources.asl')}</MenuItem>
                  <MenuItem value="Private">{t('cat.sources.private')}</MenuItem>
                  <MenuItem value="Municipality">{t('cat.sources.municipality')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('cat.municipality')}</InputLabel>
                <Select value={angelFormData.municipality || ''} label={t('cat.municipality')} onChange={(e) => setAngelFormData({ ...angelFormData, municipality: e.target.value })}>
                  <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
                  {municipalities.map((m: string) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label={t('cat.addressWhereFound')} value={angelFormData.address_where_found || ''} onChange={(e) => setAngelFormData({ ...angelFormData, address_where_found: e.target.value })} multiline rows={2} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('cat.temporaryPlacement')} value={angelFormData.temporary_placement || ''} onChange={(e) => setAngelFormData({ ...angelFormData, temporary_placement: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('cat.area')} value={angelFormData.area || ''} onChange={(e) => setAngelFormData({ ...angelFormData, area: e.target.value })} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          {/* Health Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#ffecd2', mb: 0.75, fontSize: '0.8rem' }}>
            {t('cat.health')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label={t('cat.initialHealthStatus')} value={angelFormData.initial_health_status || ''} onChange={(e) => setAngelFormData({ ...angelFormData, initial_health_status: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('cat.sterilized')}</InputLabel>
                <Select value={angelFormData.sterilized ?? 'U'} label={t('cat.sterilized')} onChange={(e) => setAngelFormData({ ...angelFormData, sterilized: e.target.value })}>
                  <MenuItem value="U">{t('common.unknown')}</MenuItem>
                  <MenuItem value="N">{t('common.no')}</MenuItem>
                  <MenuItem value="Y">{t('common.yes')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('cat.fiv')}</InputLabel>
                <Select value={angelFormData.fiv || 'Unknown'} label={t('cat.fiv')} onChange={(e) => setAngelFormData({ ...angelFormData, fiv: e.target.value })}>
                  <MenuItem value="Unknown">{t('common.unknown')}</MenuItem>
                  <MenuItem value="Y">{t('common.yes')}</MenuItem>
                  <MenuItem value="N">{t('common.no')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('cat.felv')}</InputLabel>
                <Select value={angelFormData.felv || 'Unknown'} label={t('cat.felv')} onChange={(e) => setAngelFormData({ ...angelFormData, felv: e.target.value })}>
                  <MenuItem value="Unknown">{t('common.unknown')}</MenuItem>
                  <MenuItem value="Y">{t('common.yes')}</MenuItem>
                  <MenuItem value="N">{t('common.no')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" required>
                <InputLabel>{t('cat.costClass')}</InputLabel>
                <Select value={angelFormData.cost_class || 'Ordinary'} label={t('cat.costClass')} onChange={(e) => setAngelFormData({ ...angelFormData, cost_class: e.target.value })}>
                  <MenuItem value="Ordinary">{t('cat.costClasses.ordinary')}</MenuItem>
                  <MenuItem value="Increased">{t('cat.costClasses.increased')}</MenuItem>
                  <MenuItem value="Increased Kitten">{t('cat.costClasses.increasedKitten')}</MenuItem>
                  <MenuItem value="Increased Sickroom">{t('cat.costClasses.increasedSickroom')}</MenuItem>
                  <MenuItem value="Other">{t('cat.costClasses.other')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          {/* Dates Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#ffecd2', mb: 0.75, fontSize: '0.8rem' }}>
            {t('cat.dates')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <DateField label={t('cat.arrivalDate')} value={angelFormData.arrival_date || ''} onChange={(v) => setAngelFormData({ ...angelFormData, arrival_date: v })} />
            </Grid>
            <Grid item xs={6}>
              <DateField label={t('cat.departureDate')} value={angelFormData.departure_date || ''} onChange={(v) => setAngelFormData({ ...angelFormData, departure_date: v })} />
            </Grid>
            <Grid item xs={12}>
              <DateField label={t('checkout.deathDate')} value={angelFormData.death_date || ''} onChange={(v) => setAngelFormData({ ...angelFormData, death_date: v })} />
            </Grid>
          </Grid>

          {selectedAngel?.death_certificate && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#ffecd2', mb: 0.75, fontSize: '0.8rem' }}>
                {t('checkout.deathCertificateShort')}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                href={`${API_URL}/api/angels/${selectedAngel?.id}/death-certificate`}
                target="_blank"
                sx={{ textTransform: 'none' }}
              >
                {t('checkout.viewDownload')}: {selectedAngel?.death_certificate_filename || 'death_certificate'}
              </Button>
            </>
          )}

          <Divider sx={{ my: 1 }} />

          {/* Notes & Photo Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#ffecd2', mb: 0.75, fontSize: '0.8rem' }}>
            {t('cat.notesPhoto')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label={t('cat.notes')} value={angelFormData.notes || ''} onChange={(e) => setAngelFormData({ ...angelFormData, notes: e.target.value })} multiline rows={2} />
            </Grid>
            <Grid item xs={12}>
              {/* Photo section */}
              {angelFormData.photo_file_path && !editPhotoFile && (
                <Box sx={{ mb: 1.5 }}>
                  <Box
                    component="img"
                    src={`${API_URL}${angelFormData.photo_file_path}`}
                    alt="Cat photo"
                    sx={{ width: '100%', maxWidth: 200, height: 'auto', borderRadius: 2, border: '2px solid #ffecd2', mb: 0.5 }}
                  />
                  <Typography variant="caption" color="text.secondary" display="block">{t('cat.currentPhoto')}</Typography>
                </Box>
              )}
              {editPhotoFile && (
                <Box sx={{ mb: 1.5 }}>
                  <Box
                    component="img"
                    src={URL.createObjectURL(editPhotoFile)}
                    alt="New cat photo"
                    sx={{ width: '100%', maxWidth: 200, height: 'auto', borderRadius: 2, border: '2px solid #4caf50', mb: 0.5 }}
                  />
                  <Typography variant="caption" color="success.main" display="block" fontWeight="bold">{t('cat.newPhotoSelected')}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">{editPhotoFile.name} ({(editPhotoFile.size / 1024).toFixed(2)} KB)</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" component="label" size="small" sx={{ background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', color: '#333', textTransform: 'none', fontWeight: 600, flexGrow: 1 }}>
                  {editPhotoFile ? t('cat.changePhoto') : angelFormData.photo_file_path ? t('cat.replacePhoto') : t('cat.uploadPhoto')}
                  <input type="file" hidden accept="image/*" onChange={(e) => { if (e.target.files && e.target.files[0]) setEditPhotoFile(e.target.files[0]); }} />
                </Button>
                {editPhotoFile && (
                  <Button variant="outlined" color="error" size="small" onClick={() => setEditPhotoFile(null)} sx={{ textTransform: 'none', fontWeight: 600 }}>
                    {t('common.clear')}
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditAngelOpen(false);
              setEditPhotoFile(null);
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleUpdateAngel}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
              color: '#333',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(255,236,210,0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #ffd5b8 0%, #fb9f88 100%)',
              }
            }}
          >
            {t('common.update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Medical Event Dialog */}
      <Dialog
        open={addEventOpen}
        onClose={() => {
          setAddEventOpen(false);
          setSelectedFile(null);
          setEventFormData({ event_date: getTodayDate() });
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(255,236,210,0.25)',
          }
        }}
      >
        <DialogTitle sx={{ py: 1.5, fontSize: '1rem' }}>{t('medical.addMedicalEventFor')}: {eventAngel?.name || eventAngel?.chip_id}</DialogTitle>
        <DialogContent sx={{ pt: '12px !important', pb: 1, px: 2 }}>
          <FormControl fullWidth margin="normal">
            <InputLabel>{t('medical.eventType')} *</InputLabel>
            <Select
              value={eventFormData.event_type || ''}
              label={`${t('medical.eventType')} *`}
              onChange={(e) => setEventFormData({ ...eventFormData, event_type: e.target.value })}
              required
            >
              <MenuItem value="Vaccine">{t('medical.eventTypes.vaccine')}</MenuItem>
              <MenuItem value="Sterilization">{t('medical.eventTypes.sterilization')}</MenuItem>
              <MenuItem value="Treatment">{t('medical.eventTypes.treatment')}</MenuItem>
              <MenuItem value="Delivery">{t('medical.eventTypes.delivery')}</MenuItem>
              <MenuItem value="Surgery">{t('medical.eventTypes.surgery')}</MenuItem>
              <MenuItem value="Checkup">{t('medical.eventTypes.checkup')}</MenuItem>
              <MenuItem value="test_fiv_felv">{t('medical.eventTypes.testFivFelv')}</MenuItem>
              <MenuItem value="Travel Expenses">{t('medical.eventTypes.travelExpenses')}</MenuItem>
              <MenuItem value="Other">{t('medical.eventTypes.other')}</MenuItem>
            </Select>
          </FormControl>
          <DateField
            label={t('medical.eventDate')}
            value={eventFormData.event_date || ''}
            onChange={(v) => setEventFormData({ ...eventFormData, event_date: v })}
          />
          <TextField
            fullWidth
            margin="normal"
            label={t('medical.cost')}
            type="text"
            inputMode="decimal"
            value={eventFormData.cost || ''}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) setEventFormData({ ...eventFormData, cost: v });
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            label={t('medical.veterinaryName')}
            value={eventFormData.veterinary_name || ''}
            onChange={(e) =>
              setEventFormData({ ...eventFormData, veterinary_name: e.target.value })
            }
          />
          <TextField
            fullWidth
            margin="normal"
            label={t('cat.notes')}
            multiline
            rows={3}
            value={eventFormData.notes || ''}
            onChange={(e) => setEventFormData({ ...eventFormData, notes: e.target.value })}
          />

          {/* Invoice Upload Section */}
          <Box sx={{ mt: 3, p: 2, border: '1px dashed #ccc', borderRadius: 2, bgcolor: '#f9f9f9' }}>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
              {t('medical.invoiceUploadOptional')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              {t('medical.uploadDocument')}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="contained"
                component="label"
                startIcon={<AttachFileIcon />}
                color="primary"
                sx={{ flexGrow: 1 }}
              >
                {t('common.browseFiles')}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={handleFileChange}
                />
              </Button>
              {selectedFile && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => setSelectedFile(null)}
                >
                  {t('common.clear')}
                </Button>
              )}
            </Box>

            {selectedFile && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                <Typography variant="body2" fontWeight="medium" color="primary">
                  {t('common.fileSelected')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {selectedFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('common.size')}: {(selectedFile.size / 1024).toFixed(2)} KB
                </Typography>
              </Box>
            )}

            {!selectedFile && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                {t('common.noFileSelected')}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddEventOpen(false);
              setSelectedFile(null);
              setEventFormData({ event_date: getTodayDate() });
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleAddEvent}
            variant="contained"
            disabled={!eventFormData.event_type}
            sx={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(240,147,251,0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #e082ea 0%, #e4465b 100%)',
              },
              '&:disabled': {
                background: '#ccc',
                color: '#666',
              }
            }}
          >
            {t('medical.addEvent')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore Confirmation Dialog */}
      <Dialog
        open={restoreDialogOpen}
        onClose={() => {
          setRestoreDialogOpen(false);
          setAngelToRestore(null);
        }}
      >
        <DialogTitle sx={{ py: 1.5, fontSize: '1rem' }}>{t('checkout.restoreToActive')}</DialogTitle>
        <DialogContent sx={{ pt: '12px !important', pb: 1, px: 2 }}>
          <Typography>
            {t('checkout.restoreConfirmation', { name: angelToRestore?.name })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1.5 }}>
            {t('checkout.restoreDescription')}
          </Typography>
          <DateField
            label={t('checkout.reEntryDate')}
            value={reEntryDate}
            onChange={(v: string) => setReEntryDate(v)}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {t('checkout.reEntryDateHelp')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRestoreDialogOpen(false);
              setAngelToRestore(null);
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleRestoreConfirm}
            variant="contained"
            color="primary"
          >
            {t('data.restore')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AngelsList;
